import { useCallback, useMemo, useState } from 'react';
import { ServerExportParams } from 'components/table/table-actions';
import { InlineNotification } from '@carbon/react';
import { useLoadableAtomValue } from 'lib/hooks/useLoadableAtomValue';
import { AssetRiskBanner } from 'components/asset-risk-banner';
import { TractNRIColumns } from 'components/nri-table/types';
import { FullNRITable } from 'components/nri-table/nri-table';
import { packageSlotSummaryAtom } from './atoms';
import { useEvalExportPresets } from '../eval-3-workspace/hooks';
import { AFE_COLUMNS } from './utils';
import {
  useEnhancedSlotAllocationsPaginated,
  useSlotAllocationsExport,
} from './aggregated-free-cash-flow/hooks';
import {
  SlotAllocationsSummary,
  SlotSummaryRow,
} from 'components/slot-summary-table';
import { SlotVolumeSource, SlotAfeSource } from 'types/evaluation';
import {
  SlotVolumeSourceConfiguration,
  SlotAfeSourceConfiguration,
  SlotCostDateConfiguration,
} from './aggregated-free-cash-flow/types';
import { EditVolumeSourceModal } from './edit-volume-source-modal/edit-volume-source-modal';

/**
 * Column keys for the Slot Allocations table in Enhanced Overview.
 * Based on DEFAULT_TRACT_COLUMNS with volume source columns added after slotId.
 */
const SLOT_ALLOCATIONS_COLUMNS: Array<TractNRIColumns> = [
  'crmTract',
  'crmAccount',
  'slotId',
  // New volume source columns (same order as Slots tab)
  'volumeSourceId',
  'forecastStatus',
  'forecastDate',
  'dcaScenario',
  'slotLength',
  'typeCurveAreaName',
  'typeCurveLateralLength',
  // AFE source columns
  'afeSourceId',
  'afeWellName',
  'slotContingenciesCosts',
  'contingenciesCostsDate',
  'slotFacilitiesCosts',
  'facilitiesCostsDate',
  'slotDrillingCosts',
  'drillingCostsDate',
  'slotCompletionCosts',
  'completionCostsDate',
  'slotOtherCosts',
  'otherCostsDate',
  // Rest of DEFAULT_TRACT_COLUMNS
  'unit',
  // Per-row pricing (Eval 3.0). Sit immediately before the NRI/WI columns.
  'targetNpvExcluded',
  'maxNpvExcluded',
  'targetNpvIncluded',
  'maxNpvIncluded',
  'totalSlotNri',
  'totalSlotWi',
  'allocationMethod',
  'tractNetAcres',
  'netAcreageUnits',
  'tractGrossAcres',
  'surveyArea',
  'stateAbbreviation',
  'quarterCall',
  'instrumentType',
  'interestType',
  'depthRange',
  'unitSource',
  'unitDevelopmentType',
  // Scenario-area columns, grouped immediately after the unit columns
  'dpsArea',
  'taxArea',
  'differentialArea',
  'discountRateArea',
  'expenseArea',
  'wellName',
  'wellStatus',
  'typeCurveStatus',
  'operator',
  'holeDirection',
  'formation',
  'basin',
  'tier',
  'trueVerticalDepthFeet',
  'slotNumber',
  'permitDate',
  'spudDate',
  'rigReleaseDate',
  'completionDate',
  'firstProductionDate',
  'shutInDate',
  'valued',
  'unvaluedReason',
  'titleQuality',
  'titleRiskFactor',
];

interface SlotAllocationsTabProps {
  /** The package ID */
  packageId: number;
  /** The evaluation ID (required for enhanced slot allocations) */
  evalId: number;
  /** The actual Eval 2.0 evaluation ID, used in API calls when slotVolumeSource is 'eval_2_0'. */
  eval2EvaluationId: number;
  /** Current slot volume source setting */
  slotVolumeSource: SlotVolumeSource;
  /** Current AFE source setting */
  slotAfeSource: SlotAfeSource;
  /** Selected DCA scenario ID (null = Legacy) */
  dcaScenarioId?: number | null;
  /** Eval3 calculation ID — enables serving from cached Parquet blob */
  calculationId?: number | null;
  /** Per-slot volume source configurations from edit modal */
  slotVolumeConfigurations: Record<string, SlotVolumeSourceConfiguration>;
  /** Callback when a slot's volume source is changed via edit modal */
  onSlotVolumeConfigurationChange: (
    slotId: string,
    volumeSourceId: string,
    volumeSourceType: string,
    volumeSourceHref?: string,
    forecastSource?: string,
    dcaScenario?: string
  ) => void;
  /** Per-slot AFE source configurations (read-only display in slot allocations) */
  slotAfeConfigurations?: Record<string, SlotAfeSourceConfiguration>;
  /** Per-slot cost date configurations (read-only display in slot allocations) */
  slotCostDateConfigurations?: Record<string, SlotCostDateConfiguration>;
}

/**
 * SlotAllocationsTab component for the Enhanced Overview Slot Allocations accordion.
 * Displays slot allocations summary and the enhanced NRI table with volume source columns.
 * @return {JSX.Element} The slot allocations tab component
 */
export const SlotAllocationsTab = ({
  packageId,
  evalId,
  eval2EvaluationId,
  slotVolumeSource,
  slotAfeSource,
  dcaScenarioId,
  calculationId,
  slotVolumeConfigurations,
  onSlotVolumeConfigurationChange,
  slotAfeConfigurations = {},
  slotCostDateConfigurations = {},
}: SlotAllocationsTabProps) => {
  const isScenarioMode = slotAfeSource === 'scenario';

  const filteredColumns = isScenarioMode
    ? SLOT_ALLOCATIONS_COLUMNS.filter((c) => !AFE_COLUMNS.has(c))
    : SLOT_ALLOCATIONS_COLUMNS;

  // Edit modal state
  const [editingSlotId, setEditingSlotId] = useState<string | null>(null);

  const slotAllocsColumnOrderConfigs = useEvalExportPresets(
    'slotAllocations',
    'xlsx'
  );

  // Slot summary counts only (from slot_summary endpoint - does NOT fetch slot_allocations)
  const slotSummaryData = useLoadableAtomValue<SlotSummaryRow[], Error>(
    packageSlotSummaryAtom(packageId)
  );

  // Enhanced slot NRI data (uses paginated POST /enhanced_slot_allocations)
  // Also used for SlotAllocationsSummary verification counts (eliminates redundant slot_allocations call)
  const {
    rows: tractNRI,
    isLoading: isTractNriLoading,
    error: tractNriError,
    hasMore,
    riskBanner,
    fetchNextPage,
    isFetchingMore,
    setSearch,
    currentSearch,
  } = useEnhancedSlotAllocationsPaginated({
    packageId,
    evalId,
    eval2EvaluationId,
    slotVolumeSource,
    slotAfeSource,
    dcaScenarioId,
    calculationId,
    mapMode: 'tract',
  });

  // Apply user overrides for AFE costs and cost dates
  const tractNRIWithOverrides = useMemo(() => {
    return tractNRI.map((row) => {
      if (!row.slotId) return row;
      const afeConfig = slotAfeConfigurations[row.slotId];
      const costDateOverrides = slotCostDateConfigurations[row.slotId];
      if (!afeConfig && !costDateOverrides) return row;
      return {
        ...row,
        afeWellName: afeConfig?.afeWellName ?? row.afeWellName,
        slotDrillingCosts: afeConfig?.drillingCost ?? row.slotDrillingCosts,
        slotCompletionCosts:
          afeConfig?.completionCost ?? row.slotCompletionCosts,
        slotFacilitiesCosts:
          afeConfig?.facilitiesCost ?? row.slotFacilitiesCosts,
        slotContingenciesCosts:
          afeConfig?.contingenciesCost ?? row.slotContingenciesCosts,
        slotOtherCosts: afeConfig?.otherCost ?? row.slotOtherCosts,
        drillingCostsDate:
          costDateOverrides?.drillingCostsDate ?? row.drillingCostsDate,
        completionCostsDate:
          costDateOverrides?.completionCostsDate ?? row.completionCostsDate,
        facilitiesCostsDate:
          costDateOverrides?.facilitiesCostsDate ?? row.facilitiesCostsDate,
        contingenciesCostsDate:
          costDateOverrides?.contingenciesCostsDate ??
          row.contingenciesCostsDate,
        otherCostsDate: costDateOverrides?.otherCostsDate ?? row.otherCostsDate,
      };
    });
  }, [tractNRI, slotAfeConfigurations, slotCostDateConfigurations]);

  const hasTractNriError = !!tractNriError;

  // Server-side export hook
  const { exportStatus, startExport } = useSlotAllocationsExport({
    packageId,
    eval2EvaluationId,
    calculationId: evalId,
    slotVolumeSource,
    slotAfeSource,
    dcaScenarioId,
    slotVolumeConfigurations,
  });

  // Handle export button click - passes current column filters and sort to server
  const handleServerExport = useCallback(
    ({
      filters,
      sort,
      columnOrder,
      columnPresetName,
      columnOrderExclusive,
      filenameSuffix,
    }: ServerExportParams) => {
      startExport({
        filters,
        sort,
        columnOrder,
        columnPresetName,
        columnOrderExclusive,
        filenameSuffix,
      });
    },
    [startExport]
  );

  // SlotSummary state - uses slot_summary for counts, enhanced slots for verification
  const isSlotSummaryLoading =
    slotSummaryData.state === 'loading' || isTractNriLoading;
  const hasSlotSummaryError = slotSummaryData.state === 'hasError';
  const slotSummary =
    slotSummaryData.state === 'hasData' ? slotSummaryData.data : [];

  // Find the slot being edited for modal props
  const editingSlot = editingSlotId
    ? tractNRI.find((s) => s.slotId === editingSlotId)
    : null;

  /**
   * Handles the edit button click for a slot's volume source.
   * @param {string} slotId - The ID of the slot to edit
   */
  const handleEditVolumeSource = (slotId: string) => {
    setEditingSlotId(slotId);
  };

  /**
   * Handles saving the volume source selection from the modal.
   * @param {string} volumeSourceId - The selected volume source ID
   * @param {string} volumeSourceType - The type of volume source (pdp_decline or type_curve)
   * @param {string} [volumeSourceHref] - The href for the volume source (for type curves)
   * @param {string} [forecastSource] - The forecast source ('Legacy' or 'DCA')
   * @param {string} [dcaScenario] - The DCA scenario name (for DCA forecasts)
   */
  const handleSaveVolumeSource = (
    volumeSourceId: string,
    volumeSourceType: string,
    volumeSourceHref?: string,
    forecastSource?: string,
    dcaScenario?: string
  ) => {
    if (editingSlotId) {
      onSlotVolumeConfigurationChange(
        editingSlotId,
        volumeSourceId,
        volumeSourceType,
        volumeSourceHref,
        forecastSource,
        dcaScenario
      );
      setEditingSlotId(null);
    }
  };

  const afeSourceConfig = useMemo(
    () => ({
      isEditDisabled: true,
      overriddenAfeSlotIds: isScenarioMode
        ? []
        : Object.keys(slotAfeConfigurations),
      overriddenAfeSourceIds: Object.fromEntries(
        Object.entries(slotAfeConfigurations).map(([slotId, config]) => [
          slotId,
          config.afeSourceId,
        ])
      ),
      overriddenAfeSourceHrefs: Object.fromEntries(
        Object.entries(slotAfeConfigurations)
          .filter(([, config]) => config.afeSourceHref)
          .map(([slotId, config]) => [slotId, config.afeSourceHref!])
      ),
      overriddenCostDates: slotCostDateConfigurations,
    }),
    [isScenarioMode, slotAfeConfigurations, slotCostDateConfigurations]
  );

  // Get the current volume source ID for the editing slot
  // Only use the override from atom - don't pre-select the smart default
  const currentVolumeSourceId = editingSlotId
    ? slotVolumeConfigurations[editingSlotId]?.volumeSourceId
    : undefined;

  return (
    <div>
      {hasSlotSummaryError && (
        <InlineNotification
          kind="error"
          title={slotSummaryData.error.message}
          lowContrast
        />
      )}
      {hasTractNriError && (
        <InlineNotification
          kind="error"
          title={tractNriError ?? 'Error loading slot allocations'}
          lowContrast
        />
      )}
      {exportStatus.status === 'failed' && (
        <InlineNotification
          kind="error"
          title={exportStatus.error ?? 'Export failed'}
          lowContrast
        />
      )}
      {exportStatus.status === 'processing' && (
        <InlineNotification
          kind="info"
          title="Export in progress..."
          subtitle="You will receive a download when complete."
          lowContrast
        />
      )}
      <AssetRiskBanner banner={riskBanner} />
      <SlotAllocationsSummary
        slotSummary={slotSummary}
        slots={tractNRIWithOverrides}
        isLoading={isSlotSummaryLoading}
        showHighlight={false}
        tableId="package-eval-v3-slot-allocations-summary-table"
      />
      <FullNRITable
        tractNRIRows={tractNRIWithOverrides}
        isLoading={isTractNriLoading}
        tableId="package-eval-v3-slot-allocations-table"
        tractNRIColumns={filteredColumns}
        packageId={packageId}
        volumeSourceConfig={{
          onEdit: handleEditVolumeSource,
          isEditDisabled: true,
          overriddenSlotIds: Object.keys(slotVolumeConfigurations),
          overriddenVolumeSourceIds: Object.fromEntries(
            Object.entries(slotVolumeConfigurations).map(([slotId, config]) => [
              slotId,
              config.forecastSource === 'DCA'
                ? `Insights_${config.volumeSourceId}`
                : config.volumeSourceId,
            ])
          ),
          overriddenVolumeSourceHrefs: Object.fromEntries(
            Object.entries(slotVolumeConfigurations)
              .filter(([, config]) => config.volumeSourceHref)
              .map(([slotId, config]) => [slotId, config.volumeSourceHref!])
          ),
          overriddenDcaScenarios: Object.fromEntries(
            Object.entries(slotVolumeConfigurations)
              .filter(([, config]) => config.dcaScenario)
              .map(([slotId, config]) => [slotId, config.dcaScenario!])
          ),
        }}
        afeSourceConfig={afeSourceConfig}
        onServerExport={handleServerExport}
        isServerExporting={exportStatus.status === 'processing'}
        exportPresets={slotAllocsColumnOrderConfigs}
        hasMore={hasMore}
        onLoadMore={fetchNextPage}
        isFetchingMore={isFetchingMore}
        onSearchChange={setSearch}
        searchValue={currentSearch}
      />
      <EditVolumeSourceModal
        isOpen={editingSlotId !== null}
        onClose={() => setEditingSlotId(null)}
        onSave={handleSaveVolumeSource}
        slotId={editingSlotId ?? ''}
        packageId={packageId}
        typeCurveAreaName={editingSlot?.typeCurveAreaName}
        wellName={editingSlot?.wellName}
        currentVolumeSourceId={currentVolumeSourceId}
      />
    </div>
  );
};
